/**
 * PayStack Web Integration
 * Uses PayStack Popup for card and mobile money payments
 */

declare global {
  interface Window {
    PaystackPop: {
      setup: (config: PaystackConfig) => PaystackHandler
    }
  }
}

interface PaystackConfig {
  key: string
  email: string
  amount: number // in pesewas (GHS * 100)
  currency: string
  ref: string
  channels?: string[]
  metadata?: Record<string, any>
  callback: (response: PaystackResponse) => void
  onClose: () => void
}

interface PaystackHandler {
  openIframe: () => void
}

export interface PaystackResponse {
  reference: string
  trans: string
  status: string
  message: string
  transaction: string
  trxref: string
}

/**
 * Load the PayStack inline script if not already loaded
 */
export function loadPaystackScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.PaystackPop) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://js.paystack.co/v1/inline.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load PayStack script'))
    document.head.appendChild(script)
  })
}

/**
 * Generate a unique payment reference
 */
export function generatePaymentRef(): string {
  const timestamp = Date.now()
  const random = Math.floor(Math.random() * 100000).toString().padStart(5, '0')
  return `FY-${timestamp}-${random}`
}

/**
 * Convert GHS amount to pesewas (PayStack's smallest unit)
 */
export function toPesewas(ghsAmount: number): number {
  return Math.round(ghsAmount * 100)
}

/**
 * Open PayStack payment popup
 */
export async function openPaystackPopup(params: {
  email: string
  amount: number // in GHS
  churchId: string
  churchName: string
  plan: string
  billingCycle: string
  onSuccess: (response: PaystackResponse) => void
  onClose: () => void
}): Promise<void> {
  const publicKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY

  if (!publicKey) {
    throw new Error('PayStack public key not configured. Set VITE_PAYSTACK_PUBLIC_KEY in your environment.')
  }

  await loadPaystackScript()

  const ref = generatePaymentRef()

  const handler = window.PaystackPop.setup({
    key: publicKey,
    email: params.email,
    amount: toPesewas(params.amount),
    currency: 'GHS',
    ref,
    channels: ['mobile_money', 'card', 'bank'],
    metadata: {
      church_id: params.churchId,
      church_name: params.churchName,
      plan: params.plan,
      billing_cycle: params.billingCycle,
      custom_fields: [
        {
          display_name: 'Church',
          variable_name: 'church_name',
          value: params.churchName,
        },
        {
          display_name: 'Plan',
          variable_name: 'plan',
          value: params.plan,
        },
        {
          display_name: 'Billing Cycle',
          variable_name: 'billing_cycle',
          value: params.billingCycle,
        },
      ],
    },
    callback: params.onSuccess,
    onClose: params.onClose,
  })

  handler.openIframe()
}

/**
 * Check if PayStack is configured
 */
export function isPaystackConfigured(): boolean {
  return !!import.meta.env.VITE_PAYSTACK_PUBLIC_KEY
}
